import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client';
import { notify } from '../../index';
import getUserControlProxyErrors from './getUserControlProxyErrors';
import displayDefaultGraphQLErrorMessage from './displayDefaultGraphQLErrorMessage';
import errors from './errors';
import { captureException } from '@sentry/browser';

const onErrorLink = ({ refreshToken } = {}) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    // The caller shows this operation's errors itself; an expired token is still refreshed.
    const suppressed = operation.getContext().suppressErrorNotification === true;
    // An error the caller expects as an answer (a RegExp on its message) is not reported to Sentry.
    const { expectedError } = operation.getContext();
    const method = operation.variables?.method;
    const path = operation.variables?.path;
    const writekey = operation.variables?.writekey;
    const mutation = operation.operationName;
    const mutationError = errors.graphQL[operation.operationName];
    if (graphQLErrors !== undefined && graphQLErrors.length > 0) {
      const graphQLError = graphQLErrors[0];
      let message = graphQLError.message;

      if (!(expectedError instanceof RegExp && expectedError.test(message))) captureException(new Error(message));

      try {
        message = JSON.parse(message);
      } catch (e) {
        // message is not JSON, do nothing
      }

      if (suppressed && graphQLError?.extensions?.code !== 'invalid-jwt') return;

      switch (graphQLErrors[0]?.extensions?.code) {
        case 'invalid-jwt':
          // This message comes from Hasura
          if (message === 'Could not verify JWT: JWTExpired') {
            if (refreshToken) {
              // Create an observable from the async token refresh
              return new Observable(observer => {
                let sub;
                refreshToken()
                  .then(newToken => {
                    if (newToken) {
                      const oldHeaders = operation.getContext().headers;
                      operation.setContext({
                        headers: {
                          ...oldHeaders,
                          Authorization: `Bearer ${newToken}`,
                        },
                      });

                      // Retry the operation and subscribe to the result
                      sub = forward(operation).subscribe({
                        next: data => {
                          observer.next(data);
                        },
                        error: err => {
                          observer.error(err);
                        },
                        complete: () => {
                          observer.complete();
                        },
                      });
                    } else {
                      // Token refresh failed, notify the user and inform Apollo
                      notify.error({
                        title: errors.graphQL.expiredJWT.title,
                        message: errors.graphQL.expiredJWT.message,
                        button: {
                          title: 'Refresh',
                          onClick: () => window.location.reload(),
                        },
                      });

                      observer.error(new Error('Token refresh failed'));
                    }
                  })
                  .catch(error => {
                    notify.error({
                      title: errors.graphQL.expiredJWT.title,
                      message: errors.graphQL.expiredJWT.message,
                      button: {
                        title: 'Refresh',
                        onClick: () => window.location.reload(),
                      },
                    });
                    observer.error(error);
                  });

                // Observable cleanup
                return () => {
                  if (sub) sub.unsubscribe();
                };
              });
            } else {
              notify.error({
                title: errors.graphQL.expiredJWT.title,
                message: errors.graphQL.expiredJWT.message,
                button: {
                  title: 'Refresh',
                  onClick: () => window.location.reload(),
                },
              });
            }
          } else {
            notify.error({
              title: errors.graphQL.invalidJWT.title,
              message: errors.graphQL.invalidJWT.message,
            });
          }
          break;
        case 'permission-denied':
          notify.error({
            title: errors.graphQL.permissionDenied.title,
            message: errors.graphQL.permissionDenied.message,
          });
          break;
        case 'validation-failed':
          notify.error({
            title: errors.graphQL.validationFailed.title,
            message: errors.graphQL.validationFailed.message,
          });
          break;
        case 'unexpected':
          if (mutation === 'useControlProxy' || mutation === 'useControlProxyCombined') {
            if (method && method.toUpperCase() === 'GET' && path && path.endsWith('/pipeline')) break;
            if (method && method.toUpperCase() === 'GET' && path && path.endsWith('/event_traces')) break;
            if (method && method.toUpperCase() === 'POST' && path && path.endsWith('/pipeline/diff')) break;
            if (method && method.toUpperCase() === 'POST' && path && path.endsWith('/pipeline')) break;
            if (method && method.toUpperCase() === 'DELETE' && path && path.endsWith('/pipeline')) break;
            if (method && method.toUpperCase() === 'POST' && path && path.endsWith('/test/validate_integration')) break;
            if (method && method.toUpperCase() === 'POST' && path && path.endsWith('/test/validate_event')) break;
            notify.error({
              title: getUserControlProxyErrors(method, path, writekey).title,
              message: getUserControlProxyErrors(method, path, writekey).message,
              code: message,
            });
            return;
          }

          if (mutation === 'useDashboardProxy') {
            return;
          }

          if (mutationError !== undefined) {
            notify.error({
              title: mutationError.title,
              message: mutationError.message,
            });
            return;
          }
          displayDefaultGraphQLErrorMessage();
          break;
        default:
          displayDefaultGraphQLErrorMessage();
      }
    }
    if (networkError) {
      captureException(networkError);
      if (suppressed) return;
      notify.error({
        title: errors.networkIssues.title,
        message: errors.networkIssues.message,
      });
    }
  });

export default onErrorLink;
